import { useCallback, useEffect, useRef } from 'react';
import { Storefront } from '../types';

/** Storage key prefix for funnel drafts */
const STORAGE_PREFIX = 'funnel_draft_';

/** Debounce delay for auto-save (ms) */
const SAVE_DEBOUNCE_MS = 1000;

/** Draft schema version for future migrations */
const DRAFT_VERSION = 1;

/** Max draft age before auto-discard (7 days in ms) */
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** Structure stored in localStorage */
interface FunnelDraft {
  version: number;
  funnel: Storefront;
  staticResources: Record<string, any>;
  scripts: Record<string, string | Record<string, string>>; // Scripts: { stepId: string } for standard steps, or { stepId: { variantId: string } } for split steps
  savedAt: number;
  environment: 'staging' | 'production';
}

/**
 * Generate storage key for a specific funnel
 */
function getStorageKey(funnelId: string, environment: 'staging' | 'production'): string {
  return `${STORAGE_PREFIX}${funnelId}_${environment}`;
}

/**
 * Custom hook for funnel localStorage persistence
 * 
 * Features:
 * - Auto-saves funnel state to localStorage on changes (debounced)
 * - Restores draft on page load/refresh
 * - Clears draft on explicit discard or save
 * - Environment-aware (staging vs production)
 */
export function useFunnelLocalStorage(
  funnelId: string | undefined,
  environment: 'staging' | 'production'
) {
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Load draft from localStorage
   * Returns null if no valid draft exists, draft is too old, or version mismatch
   */
  const loadDraft = useCallback((): FunnelDraft | null => {
    if (!funnelId) return null;
    
    try {
      const key = getStorageKey(funnelId, environment);
      const stored = localStorage.getItem(key);
      
      if (!stored) return null;
      
      const draft = JSON.parse(stored) as FunnelDraft;
      
      // Validate draft has required fields
      if (!draft.funnel || !draft.savedAt) {
        localStorage.removeItem(key);
        return null;
      }
      
      // Version check - clear incompatible drafts
      if (draft.version !== DRAFT_VERSION) {
        localStorage.removeItem(key);
        return null;
      }
      
      // Age check - discard drafts older than MAX_DRAFT_AGE_MS
      if (Date.now() - draft.savedAt > MAX_DRAFT_AGE_MS) {
        localStorage.removeItem(key);
        return null;
      }
      
      return draft;
    } catch {
      // Invalid JSON or localStorage error - silently fail
      return null;
    }
  }, [funnelId, environment]);

  /**
   * Save draft to localStorage (debounced)
   */
  const saveDraft = useCallback((
    funnel: Storefront,
    staticResources: Record<string, any>,
    scripts: Record<string, string | Record<string, string>>
  ) => {
    if (!funnelId) return;
    
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Debounce the save
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const key = getStorageKey(funnelId, environment);
        const draft: FunnelDraft = {
          version: DRAFT_VERSION,
          funnel,
          staticResources,
          scripts,
          savedAt: Date.now(),
          environment,
        };
        
        localStorage.setItem(key, JSON.stringify(draft));
      } catch {
        // Silently fail - localStorage might be full or disabled
      }
    }, SAVE_DEBOUNCE_MS);
  }, [funnelId, environment]);

  /**
   * Clear draft from localStorage
   */
  const clearDraft = useCallback(() => {
    if (!funnelId) return;
    
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    
    try {
      const key = getStorageKey(funnelId, environment);
      localStorage.removeItem(key);
    } catch {
      // Silently fail - localStorage might be disabled
    }
  }, [funnelId, environment]);

  /**
   * Check if a draft exists
   */
  const hasDraft = useCallback((): boolean => {
    return loadDraft() !== null;
  }, [loadDraft]);

  /**
   * Get draft age in human-readable format
   */
  const getDraftAge = useCallback((): string | null => {
    const draft = loadDraft();
    if (!draft) return null;
    
    const ageMs = Date.now() - draft.savedAt;
    const ageMinutes = Math.floor(ageMs / 60000);
    const ageHours = Math.floor(ageMinutes / 60);
    const ageDays = Math.floor(ageHours / 24);
    
    if (ageDays > 0) return `${ageDays}d ago`;
    if (ageHours > 0) return `${ageHours}h ago`;
    if (ageMinutes > 0) return `${ageMinutes}m ago`;
    return 'just now';
  }, [loadDraft]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return {
    loadDraft,
    saveDraft,
    clearDraft,
    hasDraft,
    getDraftAge,
  };
}

